import React, { useCallback, useMemo, useState } from 'react'
import { Box, Grid, Stack, TextField, Typography, Button, InputAdornment, IconButton, Tooltip } from '@mui/material';
// icons
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SearchIcon from 'common/customIcons/search';
import PromotionIcon from "common/customIcons/promotionIcon";
// utils and helpers
import { debounce, numberOnly } from "utilities/common";
import { getAgingProducts, getPromotionProducts } from "../../../redux/slices/hyperwolf/inventoryDistribution";
// custom components
import MDButton from "components/MDButton";
import { BrandFilter } from "common/filters";
import { SubCategoryFilter } from "common/filters";
import InfiniteScrollTable from "components/tables/infiniteScrollTable";
import { BoxFilter } from "common/filters";

const AgingDiscountedProductsPage = () => {
  const [activeTab, setActiveTab] = useState('aging'); // 'aging' or 'promotional'
  const [inputFilters, setInputFilters] = useState({});
  const [customFilters, setCustomFilters] = useState({});
  const [refreshTable, setRefreshTable] = useState(false);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCustomFilters({});
    setInputFilters({});
  };

  const handleChange = async (e) => {
    const { name, value } = e.target

    setCustomFilters(prev => ({ ...prev, [name]: value }));
    setRefreshTable(prev => !prev)
  }

  const handleDebounce = useCallback(debounce((event) => {
    const { name, value } = event.target;

    setCustomFilters(prev => ({ ...prev, [name]: value }));
    setRefreshTable(prev => !prev)
  }, 1000), []);

  const handleClearFilters = () => {
    setInputFilters({ search: customFilters.search || "" });
    setCustomFilters({ search: customFilters.search || "" });
    setRefreshTable(prev => !prev);
  }

  const columnDefs = useMemo(() => {
    const columns = [
      {
        headerName: 'Product Name/SKU',
        field: 'adminProductName',
        key: 'adminProductName',
        suppressMenu: false,
        flex: 2,
        sortable: true,
        cellRenderer: (params) => {
          if (!params?.data) return "-";
          const { adminProductName, sku, assets } = params?.data || {}
          const { thumbURL, name } = assets?.[0] || {}

          return <Box style={{ display: "flex", gap: "10px" }}>
            <img src={thumbURL} alt={name} width={30} height={30} />
            <Box>
              <Typography variant='body2' style={{ color: "#2E2E3A", fontWeight: "500", fontSize: "12px", padding: "0" }}>{adminProductName}</Typography>
              {sku && <Typography variant='body2' style={{ color: "#9A9AAF", fontSize: "12px", padding: "0" }}>{sku}</Typography>}
            </Box>
          </Box>
        }
      },
      {
        headerName: 'Category',
        field: 'category',
        suppressMenu: false,
        sortable: true,
        flex: 1.5,
        cellRenderer: (params) => {
          if (!params?.data) return "-";
          const { category } = params?.data || {}
          return <Box>
            <Typography variant='body2' style={{ color: "#2E2E3A", fontWeight: "500", fontSize: "12px", padding: "0" }}>{category?.name}</Typography>
          </Box>
        }
      },
      {
        headerName: 'Brand',
        field: 'brand',
        suppressMenu: false,
        sortable: true,
        flex: 1.5,
        cellRenderer: (params) => {
          if (!params?.data) return "-";
          const { brand } = params?.data || {}
          return <Box>
            <Typography variant='body2' style={{ color: "#2E2E3A", fontWeight: "500", fontSize: "12px", padding: "0" }}>{brand?.name}</Typography>
          </Box>
        }
      },
      {
        headerName: 'Quantity',
        field: 'quantity',
        suppressMenu: false,
        sortable: true,
        flex: 1,
      },
      {
        headerName: 'Price per Piece',
        field: 'pricePerPiece',
        suppressMenu: false,
        sortable: true,
        flex: 1,
        valueGetter: ({ data }) => data?.pricePerPiece ? `$${data?.pricePerPiece?.toLocaleString()}` : "-"
      },
      {
        headerName: 'Total Price',
        field: 'totalPrice',
        suppressMenu: false,
        sortable: true,
        flex: 1,
        valueGetter: ({ data }) => data?.totalPrice ? `$${data?.totalPrice?.toLocaleString()}` : "-"
      },
      // {
      //   headerName: 'Aging (Days)',
      //   field: 'agingDays',
      //   suppressMenu: false,
      //   sortable: true,
      //   flex: 1,
      //   valueGetter: ({ data }) => data?.externalData?.agingDays
      // },
      {
        headerName: 'Expiry (Days)',
        field: 'expiryDays',
        suppressMenu: false,
        sortable: true,
        flex: 1,
        valueGetter: ({ data }) => data?.externalData?.expiryDays
      },
    ];

    if (activeTab === "aging") {
      columns.splice(6, 0,
        {
          headerName: 'Promo',
          field: 'promo',
          suppressMenu: false,
          sortable: false,
          flex: 1,
          cellRenderer: ({ data }) => data?.isSale ? <PromotionIcon style={{ color: "#24CA49", fill: "#24CA49" }} /> : "-"
        }
      )
    }

    return columns;
  }, [activeTab])

  const isFilterSelected = useMemo(() => {
    return (
      Object.values(customFilters || {}).some(Boolean)
    );
  }, [customFilters]);

  return (
    <>
      <Box pt={{ xs: 2, sm: 3, lg: 4 }} pl={{ xs: 2, sm: 3, lg: 4 }} pr={{ xs: 2, sm: 3, lg: 4 }} className="cannabinoids-tabs">
        <Stack direction='row' gap={1} sx={{ background: '#ffffff' }}>
          <Button
            variant='outlined'
            className={activeTab === 'aging' ? 'active' : ''}
            sx={{ color: '#2E2E3A' }}
            onClick={() => handleTabChange('aging')}
          >
            Aging Products
          </Button>
          <Button
            variant='outlined'
            className={activeTab === 'promotional' ? 'active' : ''}
            sx={{ color: '#2E2E3A' }}
            onClick={() => handleTabChange('promotional')}
          >
            Promotional Products
          </Button>
        </Stack>
      </Box>

      <Box p={{ xs: 2, sm: 3, lg: 4 }}>
        <Grid container alignItems="center" rowSpacing={2} columnSpacing={2} mb={3}>
          <Grid item xs={12} sm={4}>
            <Typography variant='h4' fontWeight="semibold">
              {activeTab === 'aging' ? "Aging Products" : "Promotion Products"}
              <Tooltip arrow title={activeTab === "aging" ? "This section displays product aging and expiry details." : "This section lists products with active promotions."}>
                <span style={{ position: "relative", top: "-5px", left: "1px" }}>
                  <InfoOutlinedIcon style={{ width: '18px', height: '18px', fontWeight: 'bold' }} className="cur-pointer" />
                </span>
              </Tooltip>
            </Typography>
          </Grid>

          {/* Search Filter */}
          <Grid item xs={12} sm={8}>
            <Stack direction="row" justifyContent={{ xs: 'start', sm: 'end' }} flexWrap="wrap" alignItems="stretch" gap={2}>
              <TextField
                className="custom-search-field"
                placeholder="Search"
                label=""
                InputProps={{
                  startAdornment: <InputAdornment position="start">
                    <IconButton size='small'>
                      <SearchIcon width='18px' height='18px' />
                    </IconButton>
                  </InputAdornment>
                }}
                name="search"
                value={inputFilters.search}
                onChange={event => {
                  const { value } = event.target;
                  setInputFilters({ ...inputFilters, search: value })
                  handleDebounce(event)
                }}
                sx={{ minWidth: "300px" }}
              />
            </Stack>
          </Grid>
        </Grid>

        {/* Filters */}
        <Box className='cus-filter-sec' mb={2}>
          <Grid className='filter-box' container alignItems='center' justifyContent='flex-start' spacing={1.5}>
            <Grid item xs={12} md={2}>
              <BoxFilter
                value={customFilters?.boxId}
                handleChange={handleChange}
                placeholder={"box"}
                selectAllLabel={"All Boxes"}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <SubCategoryFilter
                value={customFilters?.categoryId}
                handleChange={handleChange}
                placeholder={"Category"}
                selectAllLabel={"All Categories"}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <BrandFilter
                value={customFilters?.brandId}
                handleChange={handleChange}
                placeholder={"Brands"}
                selectAllLabel={"All"}
              />
            </Grid>
            {activeTab === 'aging' && <>
              {/* <Grid item xs={12} md={2}>
                <Box className='filter-input-full'>
                  <Stack direction='row' alignItems='center'>
                    <TextField
                      placeholder="Aging Days"
                      name="agingDays"
                      value={inputFilters.agingDays || ""}
                      onChange={event => {
                        const { value } = event.target;
                        const newValue = numberOnly(value);
                        if (value && !newValue) return;

                        setInputFilters({ ...inputFilters, agingDays: newValue })
                        handleDebounce(event)
                      }}
                    />
                  </Stack>
                </Box>
              </Grid> */}
              <Grid item xs={12} md={2}>
                <Box className='filter-input-full'>
                  <Stack direction='row' alignItems='center'>
                    <TextField
                      placeholder="Expiry Days"
                      name="expiryDays"
                      value={inputFilters.expiryDays || ""}
                      onChange={event => {
                        const { value } = event.target;
                        const newValue = numberOnly(value);
                        if (value && !newValue) return;

                        setInputFilters({ ...inputFilters, expiryDays: newValue })
                        handleDebounce(event)
                      }}
                    />
                  </Stack>
                </Box>
              </Grid>
            </>}
            <Grid item xs={12} md={2}>
              <MDButton
                sx={{ minWidth: "120px", fontWeight: "normal" }}
                type="button"
                variant="outlined"
                color="dark"
                disabled={!isFilterSelected}
                onClick={handleClearFilters}
              >
                Clear All
              </MDButton>
            </Grid>
          </Grid>
        </Box>

        <div className='table-with-scroll'>
          {/* Aging Products Content */}
          {activeTab === 'aging' && (
            <InfiniteScrollTable
              tableName="Aging Products"
              getDataMethod={getAgingProducts}
              columnDefs={columnDefs}
              refreshTable={refreshTable}
              customFilters={customFilters}
              tableHeight="70vh"
            />
          )}

          {/* Promotional Products Content */}
          {
            activeTab === 'promotional' && (
              <InfiniteScrollTable
                tableName="Aging Products"
                getDataMethod={getPromotionProducts}
                columnDefs={columnDefs}
                refreshTable={refreshTable}
                customFilters={customFilters}
                tableHeight="70vh"
              />
            )
          }
        </div>
      </Box>
    </>
  )
}

export default AgingDiscountedProductsPage
